/**
 * Redux Store Configuration
 * @description Configures the Redux store with slices for managing application state related to shifts, employees, shift types, users, venues, and the current user session. Each slice reducer manages a distinct part of the app's state.
 *
 * @slices
 *   @property {Reducer} shifts - Manages state for shifts, including shift data and operations.
 *   @property {Reducer} employees - Manages state for employee data and related actions.
 *   @property {Reducer} shiftTypes - Manages state for shift types and associated logic.
 *   @property {Reducer} users - Manages user-related state, such as user details and authentication.
 *   @property {Reducer} venues - Manages state for venue data, including linked venues for users.
 *   @property {Reducer} currentUser - Manages the current user's session, authentication status, and account linking details.
 *   @property {Reducer} weather - Manages state for weather data and related actions.
 *
 * @example
 * import store from './store';
 *
 * @notes Uses `@reduxjs/toolkit`'s `configureStore` to simplify store setup and middleware configuration.
 */

import { configureStore } from '@reduxjs/toolkit';
import shiftsReducer from './slices/shiftSlice';
import employeesReducer from './slices/employeesSlice';
import shiftTypesReducer from './slices/shiftTypesSlice';
import usersReducer from './slices/usersSlice';
import venuesReducer from './venues/venuesSlice';
import currentUserReducer from './slices/currentUserSlice';
import violationsReducer from './violationsReport/violationsReportSlice';
import weatherReducer from './slices/weatherSlice';
import futureShiftsReducer from './slices/futureShiftsSlice';
import breaksReducer from './breaks/breaksSlice';
import reportsReducer from './reports/reportsSlice';
import dailyTicketsReducer from './dailyTickets/dailyTicketsSlice';
import newShiftsReducer from './shifts/shiftsSlice'; // change the name to shiftsReducer  when we complete the migration to the new shifts structure
import availabilityReducer from './availability/availabilitySlice';
import peopleReducer from './people/peopleSlice';
import notificationsReducer from './notifications/notificationsSlice';
import shiftSwapReducer from './shifts/shiftSwapSlice';

export const store = configureStore({
  reducer: {
    shifts: shiftsReducer,
    newShifts: newShiftsReducer.reducer, // change the name  from newShifts to shifts when we complete the migration to the new shifts structure
    breaks: breaksReducer,
    futureShifts: futureShiftsReducer,
    employees: employeesReducer,
    shiftTypes: shiftTypesReducer,
    users: usersReducer,
    venues: venuesReducer,
    currentUser: currentUserReducer,
    dailyTickets: dailyTicketsReducer,
    violationsReport: violationsReducer,
    weather: weatherReducer,
    reports: reportsReducer,
    people: peopleReducer,
    availability: availabilityReducer,
    notifications: notificationsReducer,
    shiftSwap: shiftSwapReducer,
  },

  // This is to stop Redux from showing warnings about Firestore Timestamps, which are not plain JS objects and can't be serialized normally.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these paths in the state
        ignoredPaths: [
          'dailyTickets.selectedDailyTicket.updatedAt',
          'dailyTickets.dailyTickets.updatedAt',
          'dailyTickets.dailyTicketsByPeriod.updatedAt',
          'newShifts.selectedShift.createdAt',
          'newShifts.selectedShift.updatedAt',
          'newShifts.selectedShift.clockedInAt',
          'newShifts.selectedShift.clockedOutAt',
          'breaks.selectedBreak.scheduledStart',
          'breaks.selectedBreak.scheduledEnd',
          'breaks.selectedBreak.actualStart',
          'breaks.selectedBreak.actualEnd',
          'availability.selectedEvent.createdAt',
          'availability.selectedEvent.updatedAt',
          'violationsReport.violations.timestamp',
          'notifications.notifications.createdAt',
          'notifications.lastDoc',
        ],
        // Ignore these action types
        ignoredActions: [
          'dailyTickets/updateDailyTicketNotes',
          'dailyTickets/fetchDailyTicketByDate/fulfilled',
          'dailyTickets/setDailyTicketsFromSnapshot',
          'notifications/fetchNotifications/fulfilled',
        ],
        // Ignore these action paths
        ignoredActionPaths: [
          'payload.lastDoc',
          'meta.arg.lastDoc'
        ],
      },
    }),
});

export default store;
